/**
 * What `::gallery{ids="…"}` renders: a grid of thumbnails for the images the model names.
 *
 * The complement to an inline embed. An answer that produced eight charts should show at reading
 * size only the ones its prose discusses, and offer the rest as pictures to open — eight
 * full-width embeds is a scroll, and a bare list of links is not a picture at all. The model
 * chooses which are which; the portal owns what a tile looks like.
 *
 * Exactly the ids listed, in the order written — never "the turn's other images", which would
 * make the same directive mean something different depending on what else the turn produced. The
 * carousel it opens still spans the turn's whole image set, so navigating out of the selection is
 * the reader's choice rather than a boundary the gallery imposes.
 */

import { Badge, Box, Image, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useArtifactSignedUrl } from '~portal/stores/artifact-store';
import type { ArtifactFileRow } from './ArtifactFilesTable';
import { useArtifactViewer } from './ArtifactViewer';
import { gallery } from './ImageGallery.css';
import { useTurnFiles } from './TurnFiles';

/** A stored artefact id, as the tool result reports it. */
const ARTIFACT_ID = /^[0-9a-f]{24}$/i;

/**
 * @desc The ids in the directive's `ids` attribute, in the order written.
 *
 * Commas, spaces or both, because the model will write it either way and a strict reading would
 * turn a formatting habit into a missing gallery. Anything that is not an artefact id is dropped
 * here rather than rendered as a broken tile: the backend's answer-hygiene is what rejects an
 * answer naming an id that does not exist, and this is only the reader's side of that.
 */
export const parseGalleryIds = (ids: string | undefined): string[] =>
  (ids ?? '')
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter((id) => ARTIFACT_ID.test(id));

export function ImageGallery({ ids }: { ids?: string }) {
  const files = useTurnFiles();
  const { open, standalone } = useArtifactViewer();
  const wanted = parseGalleryIds(ids);

  if (wanted.length === 0) return null;

  const byId = new Map(files.map((file) => [file.artifactId, file]));

  return (
    <Box className={gallery.grid}>
      {wanted.map((artifactId) => {
        const file = byId.get(artifactId);

        // An id the turn did not deliver, or delivered as something other than a picture. The
        // backend rejects both before the answer ships, so this is the floor under that rather
        // than an expected state — and a marker is the one outcome that must not be a crash.
        if (!file?.isImage) return <UnavailableTile key={artifactId} />;

        return <GalleryTile key={artifactId} file={file} onOpen={() => open(artifactId)} />;
      })}
      {standalone}
    </Box>
  );
}

function GalleryTile({ file, onOpen }: { file: ArtifactFileRow; onOpen: () => void }) {
  const { url, isLoading } = useArtifactSignedUrl(file.artifactId);

  if (isLoading) return <Skeleton className={gallery.thumb} />;
  if (!url) return <UnavailableTile />;

  return (
    <UnstyledButton className={gallery.tile} onClick={onOpen} aria-label={`Open ${file.name}`}>
      <Image src={url} alt={file.description ?? file.name} className={gallery.thumb} />
      <Box className={gallery.caption}>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {file.description ?? file.name}
        </Text>
        {file.revision > 1 && (
          <Badge size="xs" variant="light" color="gray">
            v{file.revision}
          </Badge>
        )}
      </Box>
    </UnstyledButton>
  );
}

function UnavailableTile() {
  return (
    <Box className={gallery.unavailable}>
      <IconAlertTriangle size={16} />
      <Text size="xs">Image unavailable</Text>
    </Box>
  );
}
